import React, { useEffect, useRef, useState } from 'react';

/* ── Simple markdown renderer ──────────────────────────────
   Handles: **bold**, *italic*, `code`, tables, bullet/numbered
   lists, headers, blockquotes, horizontal rules.
   No external dependency required.
   ────────────────────────────────────────────────────────── */
function MarkdownText({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line.trim())) {
      elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '10px 0' }} />);
      i++; continue;
    }

    // Heading
    const hMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const sizes = { 1: 15, 2: 14, 3: 13, 4: 12 };
      elements.push(
        <div key={i} style={{ fontWeight: 700, fontSize: sizes[level] || 13, color: 'var(--text)', margin: '10px 0 4px' }}>
          {inlineRender(hMatch[2])}
        </div>
      );
      i++; continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      elements.push(
        <div key={i} style={{ borderLeft: '3px solid var(--primary)', paddingLeft: 10, color: 'var(--text-2)', margin: '4px 0', fontStyle: 'italic', fontSize: 12 }}>
          {inlineRender(line.slice(2))}
        </div>
      );
      i++; continue;
    }

    // Table detection
    if (line.includes('|') && i + 1 < lines.length && /^\|[-:| ]+\|$/.test(lines[i + 1]?.trim() || '')) {
      const tableLines = [];
      while (i < lines.length && lines[i].includes('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      elements.push(<MdTable key={`tbl-${i}`} lines={tableLines} />);
      continue;
    }

    // Bullet list
    if (/^[-*•]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i])) {
        items.push(<li key={i} style={{ marginBottom: 3 }}>{inlineRender(lines[i].replace(/^[-*•]\s/, ''))}</li>);
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} style={{ paddingLeft: 16, margin: '4px 0', listStyle: 'disc' }}>{items}</ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={i} style={{ marginBottom: 3 }}>{inlineRender(lines[i].replace(/^\d+\.\s/, ''))}</li>);
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} style={{ paddingLeft: 18, margin: '4px 0' }}>{items}</ol>
      );
      continue;
    }

    // Empty line → paragraph break
    if (!line.trim()) {
      elements.push(<div key={i} style={{ height: 6 }} />);
      i++; continue;
    }

    // Normal paragraph text
    elements.push(
      <p key={i} style={{ margin: '2px 0', lineHeight: 1.65 }}>{inlineRender(line)}</p>
    );
    i++;
  }

  return <div style={{ fontSize: 12, color: 'var(--text)' }}>{elements}</div>;
}

function MdTable({ lines }) {
  const [header, , ...rows] = lines;
  const parseRow = (l) => l.split('|').map(c => c.trim()).filter((c, idx, arr) => idx > 0 && idx < arr.length - 1);
  const headers = parseRow(header);
  return (
    <div style={{ overflowX: 'auto', margin: '8px 0' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%', minWidth: 300 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '5px 8px', background: 'var(--bg)', borderBottom: '2px solid var(--border)', textAlign: 'left', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                {inlineRender(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid var(--border)' }}>
              {parseRow(row).map((cell, ci) => (
                <td key={ci} style={{ padding: '5px 8px', color: 'var(--text-2)', verticalAlign: 'top' }}>
                  {inlineRender(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function inlineRender(text) {
  if (!text) return '';
  // Split on bold/italic/code markers
  const parts = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<strong key={m.index}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>);
    else if (m[4]) parts.push(<code key={m.index} style={{ background: 'var(--bg)', padding: '1px 4px', borderRadius: 3, fontSize: '90%', fontFamily: 'monospace' }}>{m[4]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/* ── StudioPanel ──────────────────────────────────────────── */
export default function StudioPanel({
  chatMessages,
  chatLoading,
  chatScope,
  activeDocument,
  selectedIds,
  suggestedQuestions,
  suggestLoading,
  notes,
  onScopeChange,
  onAsk,
  onAddNote,
  onDeleteNote,
}) {
  const [activeTab, setActiveTab] = useState('chat');
  const [input, setInput] = useState('');
  const [noteText, setNoteText] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-collapse suggestions once chat starts
  useEffect(() => {
    if (chatMessages.length >= 2) setSuggestOpen(false);
  }, [chatMessages.length]);

  // Re-open suggestions when source changes (new context)
  useEffect(() => {
    setSuggestOpen(true);
  }, [activeDocument?._id, chatScope]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    onAsk(q);
    setInput('');
  };

  const handleSuggestedQ = (q) => {
    onAsk(q);
    setSuggestOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSaveToNote = (content) => {
    onAddNote(content);
    setActiveTab('notes');
  };

  const canChat = chatScope === 'portfolio' ? selectedIds.length > 0 : !!activeDocument;
  const hasSuggestions = suggestedQuestions.length > 0 || suggestLoading;
  const userInitial = 'U';

  return (
    <aside className="nb-studio">
      {/* Tab bar */}
      <div className="studio-tabs">
        <button className={`studio-tab${activeTab === 'chat' ? ' active' : ''}`} onClick={() => setActiveTab('chat')}>
          💬 Chat
        </button>
        <button className={`studio-tab${activeTab === 'notes' ? ' active' : ''}`} onClick={() => setActiveTab('notes')}>
          📝 Notes{notes.length > 0 ? ` (${notes.length})` : ''}
        </button>
      </div>

      {/* ── CHAT TAB ── */}
      {activeTab === 'chat' && (
        <div className="nb-chat">
          {/* Scope toggle row */}
          <div className="chat-subheader">
            <div className="scope-pills">
              <button className={`scope-pill${chatScope === 'single' ? ' active' : ''}`} onClick={() => onScopeChange('single')}>
                Single
              </button>
              <button
                className={`scope-pill${chatScope === 'portfolio' ? ' active' : ''}`}
                onClick={() => onScopeChange('portfolio')}
                disabled={selectedIds.length === 0}
              >
                Portfolio
              </button>
            </div>
            <span className="chat-context-label">
              {chatScope === 'portfolio'
                ? `${selectedIds.length} source${selectedIds.length !== 1 ? 's' : ''}`
                : activeDocument?.originalName
                  ? activeDocument.originalName.slice(0, 20) + (activeDocument.originalName.length > 20 ? '…' : '')
                  : 'No source open'}
            </span>
          </div>

          {/* Suggested questions — collapsible pill-bar */}
          {hasSuggestions && (
            <div className="sugg-bar">
              <button className="sugg-bar-toggle" onClick={() => setSuggestOpen((o) => !o)}>
                <span>✨ Suggested</span>
                <span className="sugg-chevron">{suggestOpen ? '▲' : '▼'}</span>
              </button>

              {suggestOpen && (
                <div className="sugg-dropdown">
                  {suggestLoading && (
                    <div className="sugg-loading">
                      <span className="spinner spinner-sm" /> Generating suggestions…
                    </div>
                  )}
                  {!suggestLoading && suggestedQuestions.slice(0, 5).map((q, i) => (
                    <button key={i} className="sugg-q-btn" onClick={() => handleSuggestedQ(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages — always scrollable, fills remaining space */}
          <div className="chat-messages">
            {!chatMessages.length && (
              <div className="chat-empty">
                <div className="chat-empty-icon">💬</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {canChat
                    ? 'Ask anything about your documents.'
                    : 'Open a source or select sources to start chatting.'}
                </div>
              </div>
            )}

            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`chat-bubble ${msg.role}`}>
                <div className={`bubble-av ${msg.role === 'user' ? 'user-av' : 'ai-av'}`}>
                  {msg.role === 'user' ? userInitial : '🤖'}
                </div>
                <div className="bubble-text">
                  {msg.role === 'assistant'
                    ? <MarkdownText text={msg.content} />
                    : msg.content}
                  {msg.role === 'assistant' && (
                    <button
                      className="save-to-note-btn"
                      onClick={() => handleSaveToNote(msg.content)}
                      title="Save to notes"
                    >
                      📌 Save to note
                    </button>
                  )}
                </div>
              </div>
            ))}

            {chatLoading && (
              <div className="chat-bubble assistant">
                <div className="bubble-av ai-av">🤖</div>
                <div className="chat-typing">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input-area">
            <form onSubmit={handleSubmit}>
              <div className="chat-input-row">
                <textarea
                  ref={inputRef}
                  className="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    !canChat
                      ? chatScope === 'portfolio' ? 'Select sources first' : 'Open a source to chat'
                      : chatScope === 'portfolio' ? 'Ask across all selected sources…' : 'Ask about this document…'
                  }
                  disabled={!canChat || chatLoading}
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                  }}
                />
                <button type="submit" className="chat-send" disabled={!canChat || chatLoading || !input.trim()}>
                  ↑
                </button>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-ph)', marginTop: 4, textAlign: 'right' }}>
                Enter to send · Shift+Enter for newline
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── NOTES TAB ── */}
      {activeTab === 'notes' && (
        <div className="notes-panel">
          <div className="notes-head">📝 Saved Notes</div>
          <div className="notes-list">
            {notes.length === 0 && (
              <div className="notes-empty">
                <div className="notes-empty-icon">📝</div>
                <div>No notes yet. Use "Save to note" on any AI response, or write below.</div>
              </div>
            )}
            {notes.map((note) => (
              <div key={note.id} className="note-card">
                <button className="note-del" onClick={() => onDeleteNote(note.id)}>×</button>
                <div style={{ fontSize: 12, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {note.text.slice(0, 300)}{note.text.length > 300 ? '…' : ''}
                </div>
                <div className="note-card-time">
                  {new Date(note.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
          <div className="notes-input-area">
            <textarea
              className="notes-textarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Write a note…"
            />
            <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => { if (noteText.trim()) { onAddNote(noteText.trim()); setNoteText(''); } }} disabled={!noteText.trim()}>
              Save note
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
